import { useEffect, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button, EmptyState, Field, FormDialog, Icon, Picker, Spinner } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useCapability } from "../../app/bandContext.js";
import {
  useBackstageItems,
  useCreateBackstageItem,
  useDeleteBackstageItem,
} from "../../app/hooks/useBackstage.js";
import { useClubSettings } from "../../app/hooks/useFanClub.js";
import { ApiError, errorMessage, formErrorMessage } from "../../app/hooks/apiError.js";
import styles from "./BackstageManagerModal.module.scss";

type CreateBackstageItemRequest = components["schemas"]["CreateBackstageItemRequest"];

/**
 * The backstage item kinds — the SERVER's vocabulary (`IFanClubComposerService.BackstageKinds` and both DB
 * CHECKs: track/video/bts/live). `kind` is an unconstrained string on the wire, so nothing in the contract
 * stops the editor offering a value the server 400s; this list is the only thing that keeps them agreeing.
 * It shipped as audio/video/image/note with `audio` selected, so the default submission failed for every
 * band and only Video worked. Labels are legacy's: "Song" is the legacy band editor's word for `track`,
 * and "Behind the scenes" / "Livestream" are the legacy fan app's section heads for `bts` / `live`.
 */
const BACKSTAGE_KINDS = [
  { value: "track", label: "Song" },
  { value: "video", label: "Video" },
  { value: "bts", label: "Behind the scenes" },
  { value: "live", label: "Livestream" },
] as const;

const DEFAULT_KIND = "track";

/** The human label for a stored kind; an unknown value (a row older than the vocabulary) shows as itself. */
function kindLabel(kind: string | null | undefined): string {
  return BACKSTAGE_KINDS.find((k) => k.value === kind)?.label ?? (kind || "Item");
}

// A canonical 8-4-4-4-12 UUID. The wire field is a Guid, so anything else is a model-binding 400 whose
// message names a JSON path rather than the field — worth catching before it leaves the form.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface BackstageManagerModalProps {
  onClose: () => void;
  /** Open the fan-club settings modal — the no-paid-tier hint's door to creating one. Optional: without it
   * the hint still says what to do, it just has no button. */
  onOpenSettings?: () => void;
}

/**
 * The Antenna backstage manager (routed `/antenna/backstage`) — the members-only perks shelf.
 * Lists the shelf (GET /antenna/backstage), adds items (POST), and removes them (DELETE /backstage/{itemId}).
 *
 * Every mutation is confirm-then-repaint: an explicit pending state, server confirmation, the server
 * error surfaced VERBATIM inline, and the list repaints only on the invalidate → GET refetch (never optimistic).
 * Managing is `antenna:manage`; a read-only seat reaching the URL sees the forbidden state, not the manager
 * (the client mirror of the server 403). Born-blank: a fresh shelf renders the designed empty state.
 *
 * Media: `mediaId` references an uploaded asset. The media-library picker ships with the core media phase
 * (Phase 6), so for now the item's media is entered by id — see the field hint. The id is checked as a UUID
 * here so a bad paste is an inline field error rather than an opaque model-binding 400.
 *
 * Members-only content needs a PAID tier to gate to (the server 400s "Create a paid tier before uploading
 * members-only backstage content"), so a band with none is told up front and the add is disabled — rather
 * than letting it fill the form and meet the refusal on submit.
 */
export function BackstageManagerModal({ onClose, onOpenSettings }: BackstageManagerModalProps) {
  const canManage = useCapability("antenna:manage");
  const list = useBackstageItems();
  const club = useClubSettings();
  const createMut = useCreateBackstageItem();
  const deleteMut = useDeleteBackstageItem();

  const [kind, setKind] = useState<string>(DEFAULT_KIND);
  const [title, setTitle] = useState("");
  const [sub, setSub] = useState("");
  const [mediaId, setMediaId] = useState("");
  const [mediaError, setMediaError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const titleRef = useRef<HTMLInputElement>(null);

  // `FormDialog` owns Escape, the focus trap and focus restore; it focuses its first focusable (the close
  // button), so claim the title field — the real entry point — once on open (parent effects run after the primitive's).
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  // Only a LOADED ladder with no paid tier is "no paid tier". While it loads, or if it failed, the add stays
  // open and the server remains the authority — its 400 still surfaces verbatim below.
  const noPaidTier = club.data != null && !(club.data.tiers ?? []).some((t) => (t.priceCents ?? 0) > 0);

  async function handleAdd() {
    if (noPaidTier) return;
    if (!title.trim()) {
      setActionError("Give the item a title before you add it.");
      titleRef.current?.focus();
      return;
    }
    const media = mediaId.trim();
    if (!media) {
      setMediaError("Add a media ID for the item.");
      return;
    }
    if (!UUID_RE.test(media)) {
      setMediaError("That isn't a media ID. Paste the whole ID, like 3f2b6c1e-8d4a-4b7e-9c21-5a0e7d9b1c34.");
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const body: CreateBackstageItemRequest = {
        kind,
        title: title.trim(),
        sub: sub.trim() ? sub.trim() : null,
        tierId: null,
        mediaId: media,
        thumbMediaId: null,
      };
      await createMut.mutateAsync(body);
      // Confirm-then-repaint: cleared only after the server persisted; the list repaints on the invalidate.
      setTitle("");
      setSub("");
      setMediaId("");
    } catch (err) {
      // formErrorMessage: the kind/tier refusals are field-level 400s whose title is just "Validation failed".
      setActionError(formErrorMessage(err, "Couldn't add that item. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(itemId: string) {
    setRemovingId(itemId);
    setActionError(null);
    try {
      await deleteMut.mutateAsync(itemId);
    } catch (err) {
      setActionError(errorMessage(err, "Couldn't remove that item. Please try again."));
    } finally {
      setRemovingId(null);
    }
  }

  const items = list.data ?? [];

  return (
    <FormDialog
      title="Backstage"
      closeLabel="Close backstage"
      // Each row acts on its own — nothing here is a draft to guard.
      dirty={false}
      size="lg"
      onClose={onClose}
      footer={
        canManage ? (
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
        ) : undefined
      }
    >
      {!canManage ? (
        <div className={styles.notice} role="note">
          <p className={styles.noticeTitle}>Not available for your role</p>
          <p className={styles.noticeText}>
            Your role on this band doesn&apos;t include managing backstage.
          </p>
          <Button variant="ghost" onClick={onClose}>
            Back to fan club
          </Button>
        </div>
      ) : (
        <>
          {/* ADD FORM */}
          <div className={styles.addForm}>
            {noPaidTier && (
              <div className={styles.tierHint} role="note">
                <p className={styles.tierHintText}>
                  Backstage is for members only, so it needs a paid tier to unlock it. Add one in fan club
                  settings first.
                </p>
                {onOpenSettings && (
                  <Button variant="ghost" size="sm" onClick={onOpenSettings}>
                    Open fan club settings
                  </Button>
                )}
              </div>
            )}
            <div className={styles.addGrid}>
              <Picker
                id="backstage-kind"
                label="Kind"
                value={kind}
                onChange={setKind}
                options={BACKSTAGE_KINDS.map((k) => ({ value: k.value, label: k.label }))}
              />
              <Field
                ref={titleRef}
                label="Title"
                placeholder="e.g. Demo take — Saltwater"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <Field
              label="Subtitle (optional)"
              placeholder="A short line of context"
              value={sub}
              onChange={(e) => setSub(e.target.value)}
            />
            <Field
              label="Media ID"
              placeholder="Paste a media ID"
              hint="The media-library picker ships with the core media phase — enter the media ID for now."
              error={mediaError}
              value={mediaId}
              onChange={(e) => {
                setMediaId(e.target.value);
                setMediaError(undefined);
              }}
            />
            <div className={styles.addActions}>
              <Button
                variant="accent"
                onClick={() => void handleAdd()}
                loading={busy}
                disabled={busy || noPaidTier}
              >
                <Icon icon={Plus} size={16} /> Add item
              </Button>
            </div>
          </div>

          {/* LIST + states */}
          <p className={styles.sectionLabel}>On the shelf</p>
          {list.isLoading ? (
            <BackstageLoading />
          ) : list.isError ? (
            <BackstageError error={list.error} onRetry={() => void list.refetch()} />
          ) : items.length === 0 ? (
            <EmptyState
              title="No backstage items yet"
              description="Add songs, videos and behind-the-scenes moments only your members can see."
            />
          ) : (
            <ul className={styles.list}>
              {items.map((it) => {
                const id = it.itemId ?? "";
                return (
                  <li key={id} className={styles.item}>
                    <span className={styles.itemBadge}>{kindLabel(it.kind)}</span>
                    <div className={styles.itemMain}>
                      <span className={styles.itemTitle}>{it.title || "Untitled"}</span>
                      <span className={styles.itemMeta}>{it.sub || it.status || "Members only"}</span>
                    </div>
                    <button
                      type="button"
                      className={styles.removeBtn}
                      aria-label={`Remove ${it.title || "item"}`}
                      disabled={removingId === id}
                      onClick={() => void handleRemove(id)}
                    >
                      {removingId === id ? <Spinner size={16} /> : <Icon icon={Trash2} size={16} />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {actionError && (
            <p className={styles.error} role="alert">
              {actionError}
            </p>
          )}
        </>
      )}
    </FormDialog>
  );
}

/** Centered spinner while the shelf loads. */
function BackstageLoading() {
  return (
    <div className={styles.center} role="status" aria-live="polite">
      <Spinner size={22} label="Loading backstage…" />
      <p className={styles.centerText}>Loading backstage…</p>
    </div>
  );
}

/** Load error: 403 → the forbidden state (the role mirror of the server deny); else the server detail + retry. */
function BackstageError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 403) {
    return (
      <EmptyState
        title="Backstage isn't available for your role"
        description="Your role on this band doesn't include the fan club."
      />
    );
  }
  const message = error instanceof Error && error.message ? error.message : "Please try again.";
  return (
    <EmptyState
      title="Couldn't load backstage"
      description={message}
      action={<Button onClick={onRetry}>Retry</Button>}
    />
  );
}
